import "./Signupform.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendSignInLinkToEmail,
  sendEmailVerification,
} from "firebase/auth";
import { auth, GoogleAuthProvider, FacebookAuthProvider } from "../Firebase";
import useCreateNewUser from "../hooks/useCreateNewUser";
import { useLogin } from "../hooks/useLogin";
import { useInAppGoogleGuard } from "./OpenInBrowserPrompt";
import useSignupAnalytics from "../hooks/useSignupAnalytics";
import DeleteIfInactiveOption from "./DeleteIfInactiveOption";
import EmailInput from './EmailInput';
import { checkEmail } from '../utils/checkEmail';
import { firstNameFromSignIn } from '../utils/firstName';
import { signInWith, takeRedirectSignIn, hasPendingRedirect } from '../utils/socialSignIn';
import SigningInOverlay from './SigningInOverlay';
import { isFacebookLoginOn, facebookEmail, facebookErrorMessage, facebookLinkEmail, linkHeldFacebook } from '../utils/facebookLogin';
import FacebookLinkNotice from './FacebookLinkNotice';
import { isInAppBrowser } from '../utils/inAppBrowser';
import { trackMeta, communityParams } from '../utils/metaPixel';

// 24 random characters — strong enough that it never needs to be known.
const randomPassword = () => {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)) + 'aA1!';
};

const Signupform = ({ community, communityId, communityName }) => {
  const [inApp] = useState(() => isInAppBrowser());
  // Where people give up on this form — admin Page Analytics.
  const track = useSignupAnalytics(communityId);
  const { guard, prompt: openInBrowserPrompt } = useInAppGoogleGuard(undefined, undefined, track);
  const [email, setEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const offerDeleteIfInactive = community?.showNoCommitmentSignup === true;
  const [deleteIfInactive, setDeleteIfInactive] = useState(false);
  const [signUpError, setSignUpError] = useState("");
  // Facebook found an existing account with this email (null = no).
  const [fbLinkEmail, setFbLinkEmail] = useState(null);
  // Back from Google / Facebook (redirect), or the pop-up just finished:
  // "Signing you in…" while the account is set up, not a flash of this form.
  const [finishing, setFinishing] = useState(() => hasPendingRedirect('signup'));
  const [emailSent, setEmailSent] = useState(false);
  const [userCreated, setUserCreated] = useState(null);
  const navigate = useNavigate();
  const { createNewUser } = useCreateNewUser();
  const { login } = useLogin();
  
  // Check if email verification should be skipped (for local testing)
  const skipEmailVerification = process.env.REACT_APP_SKIP_EMAIL_VERIFICATION === 'true';
  // Check if we should bypass email verification and go straight to onboarding
  const bypassEmailToOnboarding = process.env.REACT_APP_BYPASS_EMAIL_TO_ONBOARDING === 'true';

  const handleSubmit = async (e) => {
    e.preventDefault();
    track('submit');

    if (!termsAccepted) {
      track('submit_no_terms');
      setSignUpError("You must accept the terms and conditions to sign up.");
      return;
    }

    // Can this address receive mail at all? (Catches "gmail.con" and the like.)
    const emailOk = await checkEmail(email);
    if (!emailOk.ok) {
      track('submit_error', 'email_domain');
      setSignUpError(emailOk.reason);
      return;
    }

    try {
      // No password to choose: the account gets a random one nobody needs
      // to know, and they log in later with "Email me a sign-in link"
      // (components/EmailLinkSignIn.js) or Forgot password.
      const userCredential = await auth.createUserWithEmailAndPassword(
        email.trim(),
        randomPassword()
      );
      const user = userCredential.user;
      // Email verification logic
      if (skipEmailVerification || bypassEmailToOnboarding) {
        console.log('[Signup] Skipping email verification (environment mode)');
        const profileCreationResult = await createNewUser(user.email, "", communityId, user.uid, true, { deleteIfInactive }); // Auto-login when verification skipped
        setUserCreated(user);
        console.log('[Signup] Profile creation result:', profileCreationResult);
        proceedAfterVerification(user);
      } else {
        // Create user profile AND log them in immediately - UX will be controlled by email verification status
        console.log('[Signup] Creating user profile and logging in - UX will hide authenticated features until email verified');
        const profileCreationResult = await createNewUser(user.email, "", communityId, user.uid, true, { deleteIfInactive }); // Auto-login immediately
        setUserCreated(user);
        console.log('[Signup] Profile creation result:', profileCreationResult);
        console.log('[Signup] User logged in but will see verification-pending UX');
        console.log('[Signup] Firebase UID:', user.uid);
        
        // Skip email verification during signup - will be required later when connecting with matches
        console.log('[Signup] Proceeding to onboarding without email verification requirement');
        proceedAfterVerification(user);
      }
    } catch (error) {
      console.error('[Signup] Error:', error);
      track('submit_error', error.code || error.message);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/email-already-in-use') {
        // Already has an account: email them a link to log straight in.
        try {
          await auth.sendSignInLinkToEmail(email.trim(), {
            url: `${window.location.origin}/ProfileUpdate`,
            handleCodeInApp: true,
          });
          try { window.localStorage.setItem('emailForSignIn', email.trim()); } catch { /* ignore */ }
          track('existing_account_link_sent');
          setSignUpError(
            <span>
              <strong>You&apos;re already a member</strong> — that email is already in use. We&apos;ve sent a log-in link to it: just tap the link in your email to log straight in.
            </span>
          );
        } catch (linkError) {
          // Firebase's link failed (it did every time from here in Oct 2026 —
          // the reason is recorded now): our own log-in link instead
          // (backend routes/loginLink.js).
          track('existing_account_link_error', linkError?.code || linkError?.message || 'unknown');
          let sent = false;
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login-link`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email.trim(), communityId }),
            });
            sent = res.ok;
          } catch { /* fall through to the Log in link */ }
          if (sent) {
            track('existing_account_link_sent', 'backup');
            setSignUpError(
              <span>
                <strong>You&apos;re already a member</strong> — that email is already in use. We&apos;ve sent a log-in link to it: just tap the link in your email to log straight in.
              </span>
            );
          } else {
            setSignUpError(
              <span>
                This email is already registered.
                <a href="/login" className="signupform-error-link"> Log in here</a>.
              </span>
            );
          }
        }
      } else if (error.code === 'auth/invalid-email') {
        setSignUpError("Please enter a valid email address.");
      } else {
        setSignUpError("Sign up failed. Please check your details and try again.");
      }
    }
  };

  const proceedAfterVerification = (user) => {
    track('account_created');
    // Meta Pixel: a sign-up, tagged with this community (utils/metaPixel.js).
    trackMeta('CompleteRegistration', { ...communityParams({ id: communityId, title: communityName || community?.title, ref: community?.databaseRef }), signup_route: 'form' });
    const communityDisplayName = communityName || 'our community';
    const welcomeMessage = communityName 
      ? `Welcome to ${communityName}!` 
      : 'Welcome!';
    
    console.log(`[Signup] ${welcomeMessage} User verified, proceeding to profile setup.`);
    
    // Check if this is an additional community join (from IndividualJoin page)
    const isAdditionalCommunity = window.location.pathname.includes('/IndividualJoin/');
    
    if (isAdditionalCommunity) {
      // For additional community joins, redirect to JoinAdditionalCommunity flow
      navigate(`/JoinAdditionalCommunity/${communityId}`);
    } else {
      // For first-time signups, redirect to onboarding  
      navigate("/Onboarding");
    }
  };

  // They already had an account (the server won't make a second one):
  // logged in now — join this community if they're not in it yet.
  const continueExistingMember = () => {
    let communities = [];
    try { communities = JSON.parse(localStorage.getItem('user') || '{}').listOfCommunities || []; } catch { /* none */ }
    if (communityId && !communities.map(String).includes(String(communityId))) {
      navigate(`/JoinAdditionalCommunity/${communityId}`);
    } else {
      navigate('/ProfileUpdate');
    }
  };

  // After Google sign-in (pop-up, or back from a redirect). `untick`:
  // the "delete if inactive" tick as it was when they tapped the button.
  const finishGoogleSignIn = async (result, untick = deleteIfInactive) => {
    setFinishing(true);
    const user = result.user;
    await linkHeldFacebook(user); // after "already have an account" on Facebook
    // Google users have verified emails, so we can auto-login them
    const profileCreationResult = await createNewUser(user.email, firstNameFromSignIn(result), communityId, user.uid, true, { deleteIfInactive: untick });
    setUserCreated(user);
    console.log('[Signup] Google signup profile creation result:', profileCreationResult);
    if (skipEmailVerification || profileCreationResult?.success) {
      proceedAfterVerification(user);
      return;
    }
    // Profile creation failed — user likely already has an account. Log them in.
    console.log('[Signup] Profile creation failed, attempting login for existing user');
    if (await login(user.uid)) {
      continueExistingMember();
    } else {
      track('google_error', 'profile_or_login_failed');
      setFinishing(false);
      setSignUpError("Google sign-in failed. Please try again.");
    }
  };

  const googleError = (error) => {
    setFinishing(false);
    console.error('[Signup] Google sign-in error:', error.code, error.message);
    if (error.code === 'auth/popup-closed-by-user') {
      track('google_cancelled');
      return;
    }
    track('google_error', error.code || error.message);
    setSignUpError("Google sign-in failed. Please try again.");
  };

  const handleGoogleSignIn = async () => {
    track('google_click');
    try {
      const result = await signInWith(new GoogleAuthProvider(), { page: 'signup', extra: { deleteIfInactive } });
      if (result) await finishGoogleSignIn(result);
    } catch (error) {
      googleError(error);
    }
  };

  // After Facebook sign-in (pop-up, or back from a redirect).
  const finishFacebookSignIn = async (result, untick = deleteIfInactive) => {
    setFinishing(true);
    const user = result.user;
    track('facebook_signed_in');
    // No verification email, as with Google (the backend trusts the email
    // Facebook gives). No email at all → onboarding asks for one.
    if (!facebookEmail(user)) track('facebook_no_email');
    const profileCreationResult = await createNewUser(facebookEmail(user) || '', firstNameFromSignIn(result), communityId, user.uid, true, { deleteIfInactive: untick });
    setUserCreated(user);
    console.log('[Signup] Facebook signup profile creation result:', profileCreationResult);
    if (skipEmailVerification || profileCreationResult?.success) {
      proceedAfterVerification(user);
      return;
    }
    // Profile creation failed — they likely already have an account. Log them in.
    console.log('[Signup] Profile creation failed, attempting login for existing user');
    if (await login(user.uid)) {
      continueExistingMember();
    } else {
      track('facebook_error', 'profile_or_login_failed');
      setFinishing(false);
      setSignUpError("Facebook sign-in failed. Please try again, or use your email.");
    }
  };

  const facebookError = (error) => {
    setFinishing(false);
    console.error('[Signup] Facebook sign-in error:', error.code, error.message);
    const linkEmail = facebookLinkEmail(error);
    if (linkEmail !== null) { track('facebook_already_joined'); setFbLinkEmail(linkEmail); return; }
    const message = facebookErrorMessage(error);
    if (!message) { track('facebook_cancelled'); return; }
    track('facebook_error', error.code || error.message);
    setSignUpError(message);
  };

  const handleFacebookSignIn = async () => {
    track('facebook_click');
    setSignUpError("");
    try {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      const result = await signInWith(provider, { page: 'signup', extra: { deleteIfInactive } });
      if (result) await finishFacebookSignIn(result);
    } catch (error) {
      facebookError(error);
    }
  };

  // Back from a Google / Facebook redirect (phones, in-app browsers): carry
  // on as after a pop-up, with the tick they had set before leaving.
  useEffect(() => {
    let cancelled = false;
    takeRedirectSignIn('signup').then(async (back) => {
      if (cancelled) return;
      if (!back) { setFinishing(false); return; }
      const facebook = back.pending.provider === 'facebook';
      track(facebook ? 'facebook_redirect_back' : 'google_redirect_back');
      if (back.error) { (facebook ? facebookError : googleError)(back.error); return; }
      const untick = back.pending.deleteIfInactive === true;
      setDeleteIfInactive(untick);
      try {
        await (facebook ? finishFacebookSignIn : finishGoogleSignIn)(back.result, untick);
      } catch (error) {
        (facebook ? facebookError : googleError)(error);
      }
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResendVerification = async () => {
    if (!userCreated) {
      setSignUpError("No user account found. Please sign up first.");
      return;
    }

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/verify-email?mode=verifyEmail&continueUrl=${encodeURIComponent('/ProfileUpdate')}`,
        handleCodeInApp: false,
      };
      
      await sendEmailVerification(userCreated, actionCodeSettings);
      setSignUpError(""); // Clear any previous errors
      console.log('[Signup] Verification email resent');
      alert('Verification email has been resent! Please check your email (including spam folder).');
    } catch (error) {
      console.error('[Signup] Error resending verification email:', error);
      setSignUpError("Failed to resend verification email. Please try again.");
    }
  };

  const handleEmailLinkSignIn = async () => {
    try {
      const emailLink = window.prompt("Please enter your email for sign up");
      if (emailLink) {
        // You can add your email link logic here
        setSignUpError("Check your email for the sign-in link.");
      } else {
        setSignUpError("Email is required for sign up.");
      }
    } catch (error) {
      setSignUpError("Error sending sign-in link. Please try again.");
    }
  };

  // Inside the Facebook / Instagram (etc.) app's own browser, Google refuses
  // to sign anyone in, and almost everyone who hit the "open in your browser"
  // steps gave up (11 of 12, Oct 2026). So there: email first, no Google
  // button, and Facebook below it (by redirect — utils/socialSignIn.js).
  const facebookButton = isFacebookLoginOn() && (
    <button
      type="button"
      className="social-btn social-btn-facebook"
      onClick={handleFacebookSignIn}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
        <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.256 1.344-3.503 3.4-3.503.985 0 2.015.176 2.015.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.4 2.6H10.41v6.29A9.002 9.002 0 0 0 18 9z"/>
      </svg>
      Continue with Facebook
    </button>
  );

  return (
    <form className="signupform-container" onSubmit={handleSubmit}>
      {finishing && <SigningInOverlay />}
      {/* Google first — on small phones (and in the Facebook/Instagram browser)
          the fields below push it off screen. */}
      {!inApp && (<>
      <div className="signupform-social-buttons">
        <button
          type="button"
          className="social-btn social-btn-google"
          onClick={guard(handleGoogleSignIn)}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}>
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>
        {openInBrowserPrompt}
        {/* Hidden until the Meta app is Live — see utils/facebookLogin.js. */}
        {facebookButton}
        {fbLinkEmail !== null && <FacebookLinkNotice email={fbLinkEmail} onGoogle={guard(handleGoogleSignIn)} onClose={() => setFbLinkEmail(null)} />}
      </div>

      <div className="signupform-divider"><span>or sign up with email</span></div>
      </>)}

      <label className="signupform-label">Email address:</label>
      <EmailInput
        className="signupform-input"
        onFocus={() => track('email_focus')}
        onChange={(v) => { setEmail(v); track('email_typed'); }}
        value={email}
        required
      />

      <p className="signupform-no-password">No password needed. We&apos;ll email you a link whenever you need to log in.</p>

      {offerDeleteIfInactive && (
        <DeleteIfInactiveOption
          checked={deleteIfInactive}
          onChange={(v) => { setDeleteIfInactive(v); track(v ? 'delete_if_inactive_ticked' : 'delete_if_inactive_unticked'); }}
        />
      )}
      <label className="signupform-checkbox-label" id="signup-terms-label">
        <input
          className="signupform-checkbox"
          id="signup-terms-checkbox"
          type="checkbox"
          onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) track('terms_ticked'); }}
          checked={termsAccepted}
        />
        <span>
          {/* The site's own pages (not the old Google Docs); opening them is
              recorded for the sign-up page analytics. */}
          I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={() => track('terms_opened')}>terms and conditions</a> and the <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={() => track('privacy_opened')}>privacy policy</a>
        </span>
      </label>

      {signUpError && <p className="signupform-error">{signUpError}</p>}
      
      <button className="button" type="submit">
        Sign up
      </button>

      {/* In the app's browser: Facebook under the email form (no Google). */}
      {inApp && facebookButton && (<>
        <div className="signupform-divider"><span>or</span></div>
        <div className="signupform-social-buttons">
          {facebookButton}
          {fbLinkEmail !== null && <FacebookLinkNotice email={fbLinkEmail} onGoogle={guard(handleGoogleSignIn)} onClose={() => setFbLinkEmail(null)} />}
        </div>
      </>)}
      <p className="signupform-free-note">Free to join. Free to use.</p>

    </form>
  );
};

export default Signupform;
